'use client';
import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@mui/material';

import { type merged_time_and_activity } from '@common/@types/prisma';
import { type StudentLearningTime } from '@common/@types/learning_time';

import { Clock, getHourHandAngle } from './Clock';
import { ClockOuterRing } from './OuterRing';
import { SubjectStats } from './SubjectStats';
import { SlotsRing } from './SlotsRing';
import { TimeBackOverview } from './Overview';
import {
  getSubjectStatsMap,
  getSelectedSubjectStats,
  getNextStudySlot,
  getActiveScheduleSlot,
  getSubjectTimeSpentMap,
  getFirstHalfLearningSchedule,
  getSelectedSubjectTimeSpent,
  getTimeSaved,
  getDayAverageStats,
} from './utils';
import { type StudentScheduleSlot } from '../model';
import { scaleUpAnimation } from '../animations';

const FIVE_MINUTES = 1000 * 60 * 5;

const LayoutCenter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 24px;
`;

const Wrapper = styled.div`
  width: 1150px;
  height: 575px;
  overflow: hidden;
  position: relative;
  margin-top: 16px;
`;

const Container = styled.div`
  width: 950px;
  height: 950px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  margin-left: 90px;
  margin-top: 90px;
  transform: scale(0);
  animation: ${scaleUpAnimation} 0.7s ease-out forwards;
`;

const InnerContainer = styled.div`
  position: relative;
  width: 650px;
  height: 650px;
  border-radius: 50%;
  pointer-events: none;
  background-color: #fff;
  box-shadow: inset 0 0 40px 20px #eeeeee;
`;

type Props = {
  scheduleData: Array<StudentScheduleSlot>;
  learningDayData?: Array<merged_time_and_activity>;
  learningTimeData?: Record<string, StudentLearningTime[]>;
};

export const TimeBackSummary = ({ learningDayData, learningTimeData, scheduleData }: Props) => {
  const theme = useTheme();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState<StudentScheduleSlot | null>(null);
  const [nextStudySlot, setNextStudySlot] = useState<StudentScheduleSlot | null>(null);
  const subjectStatsMap = getSubjectStatsMap(learningDayData, theme);
  const selectedSubjectStats = getSelectedSubjectStats(subjectStatsMap, selectedSlot);
  const averageDailyStats = getDayAverageStats(subjectStatsMap);

  const firstHalfSubjects = getFirstHalfLearningSchedule(scheduleData);

  const subjectTimeSpentMap = getSubjectTimeSpentMap(learningTimeData, firstHalfSubjects);
  const selectedSubjectTimeSpent = getSelectedSubjectTimeSpent(subjectTimeSpentMap, selectedSlot);
  const timeSavedInMinutes = getTimeSaved(firstHalfSubjects, subjectStatsMap, subjectTimeSpentMap);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, FIVE_MINUTES);

    return () => {
      clearInterval(interval);
    };
  }, []);

  // Get next study session
  useEffect(() => {
    const nextSession = getNextStudySlot(scheduleData, currentTime);
    setNextStudySlot(nextSession);
  }, [currentTime]);

  // Updating selected slot on time change
  useEffect(() => {
    const activeSlot = getActiveScheduleSlot(scheduleData, currentTime);
    setSelectedSlot(activeSlot);
  }, [currentTime]);

  return (
    <>
      <TimeBackOverview currentTime={currentTime} timeSaved={timeSavedInMinutes} />
      <LayoutCenter>
        <Wrapper>
          <Container>
            <ClockOuterRing currentTime={currentTime} scheduleData={scheduleData} />
            <SlotsRing
              currentTime={currentTime}
              scheduleData={scheduleData}
              onSlotSelected={setSelectedSlot}
              subjectStatsMap={subjectStatsMap}
              subjectTimeSpentMap={subjectTimeSpentMap}
            />
            <InnerContainer>
              <SubjectStats
                selectedSubjectStats={selectedSubjectStats}
                selectedSubjectTimeSpent={selectedSubjectTimeSpent}
                selectedSubject={selectedSlot}
                nextSubject={nextStudySlot}
                currentTime={currentTime}
                averageDailyStats={averageDailyStats}
              />
              <Clock angle={getHourHandAngle(currentTime, scheduleData)} />
            </InnerContainer>
          </Container>
        </Wrapper>
      </LayoutCenter>
    </>
  );
};
